import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import CreateButton from './CreateButton';
import PageNavigator from './PageNavigator';
import AddProtocol from './AddProtocol';
import ProtocolDetails from './ProtocolDetails';
import styles from './ProtocolsTabContent.module.css';
import mockProtocolsImg from '../assets/images/mock_protocols_img.png';
import { fetchProtocols, deleteProtocol } from '../services/protocolService';
import { showSuccessNotification, showErrorNotification } from '../store/slices/notificationSlice';
import { useCachedPermissions } from '../hooks/useCachedPermissions';

const PROTOCOLS_PER_PAGE = 8;

const ProtocolsTabContent = () => {
    const dispatch = useDispatch();

    // Get permissions for offers/protocols
    const { hasAddOffers, hasDeleteOffers, hasUpdateOffers, hasShowCustomerOffer } = useCachedPermissions();

    const [currentPage, setCurrentPage] = useState(1);
    const [protocols, setProtocols] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAddProtocol, setShowAddProtocol] = useState(false);
    const [selectedProtocol, setSelectedProtocol] = useState(null);
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 8,
        results: 0,
        last_page: 1
    });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    React.useEffect(() => {
        const loadProtocols = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetchProtocols(currentPage, PROTOCOLS_PER_PAGE);
                const apiData = response.data?.data || [];
                setProtocols(apiData.map(item => ({
                    id: item._id,
                    title: item.company_name,
                    description: item.description,
                    image: item.logo
                })));
                setPagination({
                    total: response.data?.total || 0,
                    page: response.data?.page || 1,
                    limit: response.data?.limit || 8,
                    results: response.data?.results || apiData.length,
                    last_page: response.data?.last_page || 1
                });
            } catch (err) {
                setError(err.message || 'Failed to fetch offers');
                setProtocols([]);
                setPagination({
                    total: 0,
                    page: 1,
                    limit: 8,
                    results: 0,
                    last_page: 1
                });
            } finally {
                setLoading(false);
            }
        };
        loadProtocols();
    }, [currentPage]);

    const paginatedProtocols = protocols;

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) {
            setCurrentPage(page);
        }
    };

    const handleAddProtocol = () => {
        setShowAddProtocol(true);
    };

    const handleProtocolClick = (protocol) => {
        console.log('Protocol clicked:', protocol);
        setSelectedProtocol(protocol);
    };

    const handleBackToProtocols = () => {
        setSelectedProtocol(null);
        setShowAddProtocol(false);
    };

    const handleSaveProtocol = (newProtocol) => {
        setProtocols(prev => [newProtocol, ...prev]);
        setShowAddProtocol(false);
    };

    const handleCancelAddProtocol = () => {
        setShowAddProtocol(false);
    };

    const handleDeleteProtocol = async (protocolId) => {
        if (!window.confirm('Are you sure you want to delete this offer? This action cannot be undone.')) {
            return;
        }
        setDeleteError(null);
        setDeleteLoading(true);
        try {
            await deleteProtocol(protocolId);
            setProtocols(prev => prev.filter(protocol => protocol.id !== protocolId));
            setSelectedProtocol(null);
            // Show success notification
            dispatch(showSuccessNotification('Offer deleted successfully!'));
        } catch (err) {
            const errorMessage = err.message || 'Failed to delete offer';
            setDeleteError(errorMessage);
            // Show error notification
            dispatch(showErrorNotification(`Failed to delete offer: ${errorMessage}`));
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleUpdateProtocol = (updatedProtocol) => {
        setProtocols(prev => prev.map(protocol =>
            protocol.id === updatedProtocol.id ? updatedProtocol : protocol
        ));
        // Update selectedProtocol if it matches the updated one
        setSelectedProtocol(prev =>
            prev && prev.id === updatedProtocol.id ? updatedProtocol : prev
        );
    };

    // If showing add protocol form, display it
    if (showAddProtocol) {
        return (
            <AddProtocol
                onSave={handleSaveProtocol}
                onCancel={handleCancelAddProtocol}
            />
        );
    }

    // If a protocol is selected, show the details page
    if (selectedProtocol) {
        return (
            <ProtocolDetails
                protocol={selectedProtocol}
                onBack={handleBackToProtocols}
                onDelete={handleDeleteProtocol}
                onSave={handleUpdateProtocol}
                hasUpdateOffers={hasUpdateOffers}
                hasDeleteOffers={hasDeleteOffers}
                hasShowCustomerOffer={hasShowCustomerOffer}
            />
        );
    }

    return (
        <div className={styles.protocolsContainer}>
            <div className={styles.headerContainer}>
                <div className={styles.titleSection}>
                    <h2 className={styles.protocolsTitle}>Offers</h2>
                </div>
                {hasAddOffers && (
                    <CreateButton label="+ Add Offer" onClick={handleAddProtocol} />
                )}
            </div>
            {loading ? (
                <div className={styles.noProtocolsMessage}>
                    <h3>Loading...</h3>
                    <p>Fetching offers...</p>
                </div>
            ) : error ? (
                <div className={styles.noProtocolsMessage}>
                    <h3>Error</h3>
                    <p>{error}</p>
                </div>
            ) : protocols.length > 0 ? (
                <>
                    <div className={styles.protocolsGrid}>
                        {paginatedProtocols.map(protocol => (
                            <div key={protocol.id} className={styles.protocolCard} onClick={() => handleProtocolClick(protocol)}>
                                <div className={styles.protocolImage}>
                                    <img src={protocol.image} alt={protocol.title} />
                                </div>
                                <div className={styles.protocolContent}>
                                    <h3 className={styles.protocolTitle}>{protocol.title}</h3>
                                    <p className={styles.protocolDescription}>{protocol.description}</p>
                                </div>
                                {hasDeleteOffers && (
                                    <button
                                        className={styles.deleteButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteProtocol(protocol.id);
                                        }}
                                        title="Delete Protocol"
                                        disabled={deleteLoading}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3,6 5,6 21,6"></polyline>
                                            <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                                            <line x1="10" y1="11" x2="10" y2="17"></line>
                                            <line x1="14" y1="11" x2="14" y2="17"></line>
                                        </svg>
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    {pagination.last_page > 1 && (
                        <PageNavigator
                            currentPage={currentPage}
                            totalPages={pagination.last_page}
                            onPageChange={handlePageChange}
                        />
                    )}
                    {deleteLoading && <div className={styles.loading}><span className={styles.spinner}></span> Deleting protocol...</div>}
                    {deleteError && (
                        <div className={styles.errorMessage}>
                            <h3>Error</h3>
                            <p>{deleteError}</p>
                            <button className={styles.dismissError} onClick={() => setDeleteError(null)}>Dismiss</button>
                        </div>
                    )}
                </>
            ) : (
                <div className={styles.noProtocolsMessage}>
                    <h3>No Protocols</h3>
                    <p>There are currently no protocols to display.</p>
                </div>
            )}
        </div>
    );
};

export default ProtocolsTabContent; 